import { FlagProvider, useFlag, useFlags, useFlagsStatus, useUnleashContext } from '@unleash/nextjs/client';
import { ComponentProps, createContext, useContext, useEffect, useState } from 'react';

export type Feature =
  | 'release-shared-document-set'
  | 'release-zaiko'
  | 'release-hide-ryutsu'
  | 'release-delete-chat-message'
  | 'release-restrict-edit-chat-message'
  | 'release-brokerage-contract-template'
  | 'release-customer-identifications'
  | 'release-fireins'
  | 'release-user-qr-invitation'
  | 'release-prepare-assessment-by-LLM'
  | 'release-office-clerk'
  | 'release-sales-activity-report-template'
  | 'release-todo'
  | 'release-print'
  | 'release-sdp-print'
  | 'release-2025-04-22-nsl'
  | 'release-anken-schedule'
  | 'release-nsl'
  | 'release-mypage-marketing'
  | 'release-delegation-access'
  | 'release-hide-operator-info-in-chat'
  | 'release-procedure-v2'
  | 'release-handwritten-signature'
  | 'release-transfer-user'
  | 'release-anken-visibility-assigned'
  // 案件連携と、連携先の当事者を契約対象にできる売買契約の専用フロー（既定カード）の公開ガード。
  | 'release-anken-link'
  // 積水化学向け: 操作マニュアル（S3 ホストの HTML）への導線をフラグで出し分ける。
  | 'sekisui-manual-document'
  // 本人確認(KYC)2027改正 本実装の公開ガード。
  | 'release-identifications-v2'
  | 'release-identifications-ic-methods'
  | 'release-line-friend-label';

type Exact<T> = T extends null | undefined ? never : T;

export type UnleashContext = {
  organizationId?: string;
  userId?: string;
  guestId?: string;
};

export type FeatureFlagContextOptions = {
  // getMe 等が決着したか。未決着の間は不完全なコンテキストを ready にしない。
  identitySettled?: boolean;
};

const FLAGS_READY_TIMEOUT_MS = 5000;

// unleash-proxy-client の updateContext は初回 READY 待ちで、HTTP エラーでも REJECT しない。
// fetch がハング／失敗すると Promise が解決しないため、待ち側で打ち切る。
function settleWithTimeout(promise: Promise<void>, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(resolve, timeoutMs);
    void promise.then(
      () => {
        window.clearTimeout(timeoutId);
        resolve();
      },
      () => {
        window.clearTimeout(timeoutId);
        resolve();
      }
    );
  });
}

export type Props = {
  children: React.ReactNode;
  context?: UnleashContext;
};

const REFRESH_INTERVAL = 120;
export function FeatureFlagProvider(props: Props) {
  const { children, context = {} } = props;

  // NEXT_PUBLIC_* は Next.js のビルド時に静的埋め込みされるため、ここでは process.env 直接参照を許容する。
  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
  const config: Pick<
    Exact<ComponentProps<typeof FlagProvider>['config']>,
    'url' | 'clientKey' | 'appName' | 'refreshInterval' | 'environment'
  > = {
    url: process.env.NEXT_PUBLIC_UNLEASH_URL ?? '',
    clientKey: process.env.NEXT_PUBLIC_UNLEASH_CLIENT_KEY ?? '',
    appName: process.env.NEXT_PUBLIC_UNLEASH_APP_NAME ?? '',
    refreshInterval: REFRESH_INTERVAL,
    environment: process.env.NEXT_PUBLIC_UNLEASH_ENVIRONMENT ?? '',
  };
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

  return (
    <FlagProvider
      config={{
        ...config,
        context,
      }}
    >
      {children}
    </FlagProvider>
  );
}

// ユーザー／組織コンテキストを Unleash に反映し、反映完了（updateContext の解決）を isContextReady で返す。
// flagsReady（初回取得）だけではコンテキスト適用前の評価値を掴みうるため、
// フラグ値で遷移先を確定する処理は useFeatureFlagsReady() を待つこと。
export function useFeatureFlagContext(
  context: UnleashContext,
  options?: FeatureFlagContextOptions
): { isContextReady: boolean } {
  const updateContext = useUnleashContext();
  const identitySettled = options?.identitySettled ?? false;
  // 反映済みコンテキストのキー。現在のコンテキストと同期比較することで、
  // コンテキスト変更（代理ログイン等）直後のレンダーで「新 me + 旧フラグ」が ready 扱いになるのを防ぐ
  const contextKey = `${context.organizationId ?? ''}|${context.userId ?? ''}|${context.guestId ?? ''}`;
  const [appliedContextKey, setAppliedContextKey] = useState<string | null>(null);
  const hasIdentity = Boolean(context.organizationId) && Boolean(context.userId || context.guestId);

  useEffect(() => {
    let cancelled = false;

    async function run(nextContext: UnleashContext) {
      // ID なしのときは空コンテキストで置き換え、前利用者の organizationId などが残らないようにする
      const updatePromise = updateContext(nextContext);
      // settleWithTimeout が先に解決したあとの遅延 reject を unhandled にしない
      void updatePromise.catch((error) => {
        console.error('Error updating context', error);
      });
      await settleWithTimeout(updatePromise, FLAGS_READY_TIMEOUT_MS);
      // 失敗・タイムアウト時も待ち続けないよう ready にする（フラグは初回取得値で評価される）
      if (!cancelled) {
        setAppliedContextKey(contextKey);
      }
    }

    if (!hasIdentity && !identitySettled) {
      // 読み込み中は未完了へ戻す
      setAppliedContextKey(null);
      return () => {
        cancelled = true;
      };
    }

    void run(
      hasIdentity
        ? {
            organizationId: context.organizationId,
            userId: context.userId,
            guestId: context.guestId,
          }
        : {}
    );

    return () => {
      cancelled = true;
    };
  }, [
    hasIdentity,
    identitySettled,
    context.organizationId,
    context.userId,
    context.guestId,
    contextKey,
    updateContext,
  ]);

  return { isContextReady: appliedContextKey === contextKey };
}

const FeatureFlagReadyContext = createContext(false);

// useFeatureFlagContext の isContextReady を配下に配る Provider（_app の Main で使う）
export const FeatureFlagReadyProvider = FeatureFlagReadyContext.Provider;

// フラグ値に基づいて遷移先などを「確定」してよいか。初回取得済み かつ ユーザー／組織コンテキスト反映済み。
export function useFeatureFlagsReady(): boolean {
  const flagsStatus = useFlagsStatus();
  const flagsReady = flagsStatus.flagsReady === true;
  const flagsFailed = flagsStatus.flagsError != null && flagsStatus.flagsError !== false;
  const isContextReady = useContext(FeatureFlagReadyContext);
  const [timedOut, setTimedOut] = useState(false);

  const isFullyReady = (flagsReady || flagsFailed) && isContextReady;

  useEffect(() => {
    if (isFullyReady) {
      return;
    }
    // 代理ログイン等でコンテキストが変わったら、前回のタイムアウトを持ち越さない
    setTimedOut(false);
    const timeoutId = window.setTimeout(() => {
      setTimedOut(true);
    }, FLAGS_READY_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isFullyReady]);

  // フラグ取得の遅延は timedOut で解く。コンテキスト未反映のまま入口を確定しない
  // （updateContext 側のタイムアウトが isContextReady を立てる）。
  return isFullyReady || (timedOut && isContextReady);
}

export function useFeatureFlag(feature: Feature) {
  const isEnabled = useFlag(feature);
  return isEnabled;
}

export { useFlags };
